import React, { useEffect, useState } from 'react';
import { Eye, Users, Megaphone, Search, Link2, MousePointerClick } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis, Legend } from 'recharts';
import { StatCard } from '@/components';
import { siteAnalyticsApi, SiteAnalyticsResponse } from '@/services/siteAnalyticsApi';
import toast from 'react-hot-toast';

type QuickRange = 'today' | 'thisMonth' | 'custom';

const SOURCE_CONFIG = [
  { key: 'ads' as const, label: 'Reklam', icon: Megaphone, barClass: 'bg-blue-600' },
  { key: 'organic' as const, label: 'Organik Arama', icon: Search, barClass: 'bg-green-600' },
  { key: 'referral' as const, label: 'Referans', icon: Link2, barClass: 'bg-amber-500' },
  { key: 'direct' as const, label: 'Doğrudan', icon: MousePointerClick, barClass: 'bg-gray-400' },
];

const toDateStr = (d: Date) => d.toISOString().split('T')[0];

export const SiteAnalytics: React.FC = () => {
  const [quickRange, setQuickRange] = useState<QuickRange>('today');
  const [startDate, setStartDate] = useState(toDateStr(new Date()));
  const [endDate, setEndDate] = useState(toDateStr(new Date()));
  const [data, setData] = useState<SiteAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadStats();
  }, [startDate, endDate]);

  const loadStats = async () => {
    try {
      setLoading(true);
      const result = await siteAnalyticsApi.getSiteStats(startDate, endDate);
      setData(result);
    } catch (error: any) {
      toast.error(error.message || 'Ziyaretçi istatistikleri yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const applyQuickRange = (range: QuickRange) => {
    setQuickRange(range);
    const now = new Date();
    if (range === 'today') {
      setStartDate(toDateStr(now));
      setEndDate(toDateStr(now));
    } else if (range === 'thisMonth') {
      setStartDate(toDateStr(new Date(now.getFullYear(), now.getMonth(), 1)));
      setEndDate(toDateStr(now));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Ziyaretçi İstatistikleri</h1>
        <p className="text-gray-600 mt-1">Siteye giren tekil ziyaretçi ve sayfa görüntülenme sayıları</p>
      </div>

      <div className="bg-white p-4 rounded-xl border shadow-sm flex flex-wrap items-end gap-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => applyQuickRange('today')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              quickRange === 'today' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Bugün
          </button>
          <button
            type="button"
            onClick={() => applyQuickRange('thisMonth')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
              quickRange === 'thisMonth' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Bu Ay
          </button>
        </div>

        <div className="flex items-end gap-2">
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Başlangıç</label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => {
                setQuickRange('custom');
                setStartDate(e.target.value);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Bitiş</label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => {
                setQuickRange('custom');
                setEndDate(e.target.value);
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <StatCard
          title="Toplam Görüntülenme"
          value={data?.totals.totalViews ?? 0}
          change={0}
          color="blue"
          icon={<Eye size={22} />}
        />
        <StatCard
          title="Tekil Ziyaretçi"
          value={data?.totals.uniqueVisitors ?? 0}
          change={0}
          color="green"
          icon={<Users size={22} />}
        />
      </div>

      <div className="bg-white p-6 rounded-xl border shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Kaynak Dağılımı</h2>
        <div className="space-y-3">
          {SOURCE_CONFIG.map(({ key, label, icon: Icon, barClass }) => {
            const count = data?.totals.bySource[key] ?? 0;
            const total = data?.totals.uniqueVisitors || 0;
            const percent = total > 0 ? Math.round((count / total) * 100) : 0;
            return (
              <div key={key} className="flex items-center gap-3">
                <Icon size={18} className="text-gray-400 flex-shrink-0" />
                <span className="w-32 text-sm text-gray-700 flex-shrink-0">{label}</span>
                <div className="flex-1 bg-gray-100 rounded-full h-2.5 overflow-hidden">
                  <div className={`h-full rounded-full ${barClass}`} style={{ width: `${percent}%` }} />
                </div>
                <span className="w-16 text-right text-sm font-medium text-gray-900">{count}</span>
                <span className="w-12 text-right text-xs text-gray-400">%{percent}</span>
              </div>
            );
          })}
        </div>
        <p className="text-xs text-gray-400 mt-4">
          Tekil ziyaretçilerin ilk geliş kaynağına göre kırılımı. "Reklam" satırı Google Ads tıklamasından (gclid) gelenleri gösterir.
        </p>
      </div>

      <div className="bg-white p-6 rounded-xl border shadow-sm">
        <h2 className="text-lg font-semibold mb-4">Günlük Dağılım</h2>
        {loading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={320}>
            <BarChart data={data?.daily ?? []}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis allowDecimals={false} />
              <Tooltip />
              <Legend />
              <Bar dataKey="totalViews" fill="#2563eb" name="Görüntülenme" radius={[6, 6, 0, 0]} />
              <Bar dataKey="uniqueVisitors" fill="#16a34a" name="Tekil Ziyaretçi" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

export default SiteAnalytics;
